/**
 * A12 — Comparatifs & Évolution: FE form ↔ BE situation_comparatifs_evolution mapping.
 * Form fields only on write (preserves analysis-generated columns on PUT).
 */

export const COMPARATIFS_FORM_FIELDS = [
  'comparatif_ref',
  'titre_comparatif',
  'description_comparatif',
  'type_comparatif',
  'categorie_analyse',
  'exercice_fiscal',
  'periode_reference_debut',
  'periode_comparaison_debut',
  'granularite',
  'statut_analyse',
  'confidentialite_niveau',
  'notes',
];

/** Loaded for display/filtering; never sent on POST/PUT. */
export const COMPARATIFS_DISPLAY_FIELDS = [
  'tendances_direction',
  'variance_favorable',
  'variance_defavorable',
  'alertes_tendances',
  'validation_controleur',
  'validation_direction',
  'created_at',
];

const DATE_FIELDS = new Set([
  'periode_reference_debut',
  'periode_comparaison_debut',
]);

const FORM_DEFAULTS = Object.freeze({
  type_comparatif: 'Exercice',
  categorie_analyse: 'Global',
  granularite: 'Annuelle',
  statut_analyse: 'Brouillon',
  confidentialite_niveau: 'Interne',
});

export const EMPTY_COMPARATIFS_FORM = Object.freeze({
  ...COMPARATIFS_FORM_FIELDS.reduce((acc, field) => {
    acc[field] = FORM_DEFAULTS[field] ?? '';
    return acc;
  }, {}),
  ...COMPARATIFS_DISPLAY_FIELDS.reduce((acc, field) => {
    acc[field] = '';
    return acc;
  }, {}),
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const toFormScalar = (field, value) => {
  if (value == null) return '';
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  return value;
};

/** Map API row → form state (editable + display columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_COMPARATIFS_FORM };
  [...COMPARATIFS_FORM_FIELDS, ...COMPARATIFS_DISPLAY_FIELDS].forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

/** Map form state → API payload (typed, omits audit/analysis columns). */
export const buildComparatifsPayload = (formData = {}, { forUpdate = false } = {}) => {
  const payload = {};

  COMPARATIFS_FORM_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (field === 'comparatif_ref') {
      if (!forUpdate && (raw === '' || raw == null)) return;
      if (forUpdate) return;
    }

    if (DATE_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    if (raw === '' || raw == null) {
      if (FORM_DEFAULTS[field] != null) {
        payload[field] = FORM_DEFAULTS[field];
      } else {
        payload[field] = null;
      }
      return;
    }

    payload[field] = raw;
  });

  if (!forUpdate && !payload.titre_comparatif) {
    throw new Error('titre_comparatif is required');
  }
  if (!forUpdate && !payload.exercice_fiscal) {
    throw new Error('exercice_fiscal is required');
  }

  return payload;
};
